import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useFriedhof } from '../../contexts/FriedhofContext';
import { brand } from '../../config/brand';
import { Icon } from '../ui';
import friedhofService from '../../services/friedhofService';
import './Sidebar.css';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onToggle: () => void;
}

/**
 * Seitenleiste. Ab Tablet-Breite immer da: ausgeklappt mit Logo und Beschriftung, eingeklappt als
 * schmale Symbolleiste (Emblem + Menüsymbole, Name als Tooltip). Eine Kopfzeile gibt es nicht –
 * jede Seite trägt ihren eigenen Titel. Auf dem Handy legt sie sich als Overlay über den Inhalt.
 */
const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const { friedhofId, friedhofName, resetFriedhofSelection, anzahlFriedhoefe, setAnzahlFriedhoefe } = useFriedhof();

  // Ohne sichtbaren Scrollbalken zeigt ein Ausblenden am unteren Rand, dass noch Einträge folgen –
  // nur solange wirklich etwas darunter liegt.
  const navRef = useRef<HTMLElement>(null);
  const [mehrDarunter, setMehrDarunter] = useState(false);
  const pruefeUeberlauf = useCallback(() => {
    const n = navRef.current;
    if (n) setMehrDarunter(n.scrollTop + n.clientHeight < n.scrollHeight - 2);
  }, []);
  useEffect(() => {
    pruefeUeberlauf();
    window.addEventListener('resize', pruefeUeberlauf);
    return () => window.removeEventListener('resize', pruefeUeberlauf);
  }, [pruefeUeberlauf]);

  // Nach einem Neuladen der Seite ist die Zahl noch unbekannt – einmal nachfragen.
  useEffect(() => {
    if (anzahlFriedhoefe !== null) return;
    friedhofService.getAll().then(l => setAnzahlFriedhoefe(l.length)).catch(() => { /* Knopf bleibt aus */ });
  }, [anzahlFriedhoefe, setAnzahlFriedhoefe]);
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Nach Arbeitsbereich gruppiert: oben das tägliche Nachschlagen, darunter die Abrechnung,
  // dann die selten geänderten Stammdaten und zuletzt die Administration (nur ADMIN).
  const menuGroups = [
    {
      title: null, // Einstieg – ohne Überschrift direkt unter dem Friedhof-Wechsler
      items: [
        { path: '/dashboard', label: 'Dashboard', icon: <Icon.Dashboard className="h-5 w-5" /> },
        { path: friedhofId ? `/lageplan/${friedhofId}` : '/lageplan', label: 'Lageplan', icon: <Icon.Map className="h-5 w-5" /> },
      ],
    },
    {
      title: 'Tagesgeschäft',
      items: [
        { path: '/graeber', label: 'Gräber', icon: <Icon.Grabstein className="h-5 w-5" /> },
        { path: '/tiere', label: 'Tiere', icon: <Icon.Paw className="h-5 w-5" /> },
        { path: '/kunden', label: 'Kunden', icon: <Icon.Kunden className="h-5 w-5" /> },
      ],
    },
    {
      title: 'Abrechnung',
      items: [
        { path: '/rechnungen', label: 'Rechnungen', icon: <Icon.Rechnung className="h-5 w-5" /> },
        { path: '/artikel', label: 'Artikel', icon: <Icon.Artikel className="h-5 w-5" /> },
      ],
    },
    {
      title: 'Stammdaten',
      items: [
        { path: '/friedhoefe', label: 'Friedhöfe', icon: <Icon.Baum className="h-5 w-5" /> },
      ],
    },
    ...(isAdmin() ? [{
      title: 'Administration',
      items: [
        { path: '/rechnungsdaten', label: 'Rechnungsdaten', icon: <Icon.Gebaeude className="h-5 w-5" /> },
        { path: '/verwaltung', label: 'Verwaltung', icon: <Icon.Regler className="h-5 w-5" /> },
        { path: '/aktivitaet', label: 'Aktivität', icon: <Icon.Verlauf className="h-5 w-5" /> },
        { path: '/users', label: 'Benutzerverwaltung', icon: <Icon.Schild className="h-5 w-5" /> },
      ],
    }] : []),
  ];

  // Seiten ohne eigenen Menüpunkt hängen an ihrem Bereich: ein Vertrag gehört zu den Kunden.
  const bereich = location.pathname.startsWith('/vertraege/') ? '/kunden' : location.pathname;
  const istAktiv = (pfad: string) =>
    bereich === pfad || (pfad !== '/dashboard' && bereich.startsWith(pfad));

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleSwitchFriedhof = () => {
    resetFriedhofSelection();
    onClose();
  };

  // On phones the sidebar is an overlay, so close it after navigating; on tablet/desktop
  // it stays open (the user collapses it explicitly via the header toggle).
  const closeOnMobile = () => { if (window.innerWidth < 769) onClose(); };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && <div className="sidebar-overlay" onClick={onClose} />}

      <aside className={`sidebar ${isOpen ? 'sidebar-open' : 'sidebar-eingeklappt'}`}>
        <div className="sidebar-header">
          {/* Nur das Emblem – ohne Logo bleibt der Kurzname als Text. Die Größe setzt das CSS
              (groß ausgeklappt, klein in der Symbolleiste). */}
          <div className="sidebar-brand">
            {/* Das Logo führt zum Dashboard – wie man es von Websites gewohnt ist. */}
            <Link to="/dashboard" onClick={closeOnMobile} className="sidebar-logo-link" aria-label="Zum Dashboard" title="Zum Dashboard">
              {brand.logo
                ? <img src={brand.logo} alt="" className="sidebar-logo" />
                : <h2>{brand.shortName}</h2>}
            </Link>
          </div>
          <button
            className="sidebar-close"
            onClick={onToggle}
            aria-label={isOpen ? 'Menü einklappen' : 'Menü ausklappen'}
            title={isOpen ? 'Menü einklappen' : 'Menü ausklappen'}
          >
            {isOpen ? <Icon.ChevronLeft className="h-5 w-5" /> : <Icon.ChevronRight className="h-5 w-5" />}
          </button>
        </div>

        {/* Aktiver Friedhof mit Wechsel – nur, wenn es mehrere gibt. Bei einem steht sein Name
            ohnehin auf dem Dashboard, der Abschnitt wäre nur Ballast. */}
        {(anzahlFriedhoefe ?? 0) > 1 && (
          <div
            className="sidebar-friedhof"
            style={{
              padding: '12px 16px',
              borderBottom: '1px solid var(--sidebar-border)',
              display: 'flex', flexDirection: 'column', gap: 6,
            }}
          >
            <span style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--sidebar-role-text)' }}>
              Friedhof
            </span>
            <span style={{ fontWeight: 600, color: '#f8fafc', fontSize: 14 }}>
              {friedhofName || '—'}
            </span>
            <button
              onClick={handleSwitchFriedhof}
              style={{
                marginTop: 4, alignSelf: 'flex-start', fontSize: 12, fontWeight: 600,
                color: 'var(--sidebar-brand-text)', background: 'transparent', border: 'none', padding: 0,
              }}
            >
              <Icon.Wechseln className="mr-1 inline h-3.5 w-3.5 align-[-2px]" />Friedhof wechseln
            </button>
          </div>
        )}

        <nav ref={navRef} onScroll={pruefeUeberlauf} className={`sidebar-nav ${mehrDarunter ? 'sidebar-nav--mehr' : ''}`}>
          {menuGroups.map((group, i) => (
            <div key={group.title ?? `gruppe-${i}`} className="sidebar-group">
              {group.title && <div className="sidebar-group-title">{group.title}</div>}
              {group.items.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`sidebar-link ${istAktiv(item.path) ? 'active' : ''}`}
                  onClick={closeOnMobile}
                  title={isOpen ? undefined : item.label}
                >
                  <span className="sidebar-icon">{item.icon}</span>
                  <span className="sidebar-label">{item.label}</span>
                </Link>
              ))}
            </div>
          ))}
        </nav>

        {/* User Menu */}
        <div className="sidebar-footer">
          <div className="user-section">
            <div
              className="user-info"
              onClick={() => setShowUserMenu(!showUserMenu)}
            >
              <div className="user-avatar">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
              <div className="user-details">
                <div className="user-name">
                  {user?.firstName} {user?.lastName}
                </div>
                <div className="user-role">
                  {user?.role === 'ADMIN' ? 'Administrator' : 'Benutzer'}
                </div>
              </div>
              <div className="user-menu-toggle">
                {showUserMenu ? <Icon.ChevronDown className="h-4 w-4" /> : <Icon.ChevronUp className="h-4 w-4" />}
              </div>
            </div>

            {showUserMenu && (
              <div className="user-menu">
                <Link
                  to="/change-password"
                  className="user-menu-item"
                  onClick={() => { setShowUserMenu(false); onClose(); }}
                >
                  <span className="user-menu-icon"><Icon.Schluessel className="h-[18px] w-[18px]" /></span>
                  <span>Passwort &auml;ndern</span>
                </Link>
                <button
                  className="user-menu-item"
                  onClick={handleLogout}
                >
                  <span className="user-menu-icon"><Icon.Abmelden className="h-[18px] w-[18px]" /></span>
                  <span>Abmelden</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
